import { OAUTH_STATE_KEY } from "../auth/constants";

import type { TAuthConfig } from "react-oauth2-code-pkce";

// Same local default apiClient.ts uses when VITE_LOA_SERVICE_URL is unset.
const API_SERVICE_URL =
  (import.meta.env.VITE_LOA_SERVICE_URL as string) || "https://api.loa.localhost/api/v1";

/** The backend logout endpoint on the same origin as the API service URL. */
export function logoutUrlFromServiceUrl(serviceUrl: string): string {
  return `${new URL(serviceUrl).origin}/api/auth/logout/`;
}

// Derived from the API origin rather than hard-coded to localhost: a deployed
// build whose env file omits VITE_OAUTH_LOGOUT_URL must still log out against
// its own API, or the Django session survives and the next load signs the
// user straight back in (LOA-1540, .env.dev on loa-mitratech.io).
export const LOGOUT_URL =
  (import.meta.env.VITE_OAUTH_LOGOUT_URL as string) || logoutUrlFromServiceUrl(API_SERVICE_URL);

/** django-oauth-toolkit's standard RFC 7009 revocation endpoint, same origin as the API. */
export function revokeUrlFromServiceUrl(serviceUrl: string): string {
  return `${new URL(serviceUrl).origin}/o/revoke_token/`;
}

// LOA-1613: revokes this session's own access/refresh token on logout, rather
// than the backend's logout view, which (by design) only flushes the Django
// session and does not revoke tokens for a plain same-tenant logout.
export const REVOKE_URL =
  (import.meta.env.VITE_OAUTH_REVOKE_URL as string) || revokeUrlFromServiceUrl(API_SERVICE_URL);

export const oauthConfig: TAuthConfig = {
  clientId: (import.meta.env.VITE_OAUTH_CLIENT_ID as string) || "loa-frontend-spa",
  authorizationEndpoint:
    (import.meta.env.VITE_OAUTH_AUTHORIZE_URL as string) || "https://api.loa.localhost/o/authorize/",
  tokenEndpoint:
    (import.meta.env.VITE_OAUTH_TOKEN_URL as string) || "https://api.loa.localhost/o/token/",

  // Locked at root — the IdP registration cannot be changed at this time.
  // /auth/callback is a UX-only route; the real OAuth response always lands here.
  // See: docs/adr/002-oauth-callback-at-root.md
  redirectUri: typeof window !== "undefined" ? window.location.origin + "/" : "",

  scope: "openid profile email read write",
  // Tokens are stored in localStorage (the library only supports
  // "session" | "local"). Required for single-client session
  // enforcement: sessionStorage isolates each tab, which forces a
  // re-login on every new tab and hides the cross-tab session from
  // hasStoredOAuthToken()/the active-tenant check (see auth/utils.ts).
  //
  // Security tradeoff (intentional): localStorage persists across
  // browser restarts, so an XSS could read a still-valid token for
  // longer than with sessionStorage. Mitigations: access tokens are
  // short-lived (1h, oauth2_provider ACCESS_TOKEN_EXPIRE_SECONDS),
  // tokens are opaque (decodeToken: false), and logout() purges all
  // ROCP_* keys + the active-tenant cookie. Residual exposure is the
  // 7-day refresh token; shorten its TTL server-side to reduce it.
  storage: "local",
  clearURL: true,
  autoLogin: false,
  decodeToken: false,

  // Called just before the browser redirects to the IdP.
  // Returns the state string that the IdP will echo back in the callback URL.
  // login.tsx writes the encoded OAuthState to sessionStorage before calling logIn(),
  // so we read it here and pass it through the OAuth round-trip.
  preLogin: (): string => {
    return sessionStorage.getItem(OAUTH_STATE_KEY) ?? "";
  },

  onRefreshTokenExpire: (event: { logIn: () => void }): void => {
    sessionStorage.removeItem(OAUTH_STATE_KEY);
    event.logIn();
  },
};
